import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { getAllCompanies } from '../services/CompanyService';
import { extractApiError } from '../utils/apiError';
import '../styles/CustomerPage.css';
import CustomerModal from '../modals/CustomerModal';
import { useToast, useConfirm } from '../context/UIContext';

export default function Customers() {
    const toast = useToast();
    const confirm = useConfirm();
    const [customers, setCustomers] = useState([]);
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editCustomer, setEditCustomer] = useState(null);
    const [companyFilter, setCompanyFilter] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => { loadCustomers(); }, []);

    const loadCustomers = () => {
        setIsLoading(true);
        getAllCompanies()
            .then(data => { setCustomers(data); setFilteredCustomers(data); })
            .catch(err => toast(extractApiError(err, 'Fehler beim Laden der Kunden.')))
            .finally(() => setIsLoading(false));
    };

    const handleAdd = () => { setEditCustomer(null); setShowModal(true); };
    const handleEdit = (customer) => { setEditCustomer(customer); setShowModal(true); };

    const handleDelete = async (id) => {
        if (await confirm('Kunde wirklich löschen?', { confirmLabel: 'Löschen', danger: true })) {
            api.delete(`/Company/${id}`)
                .then(loadCustomers)
                .catch(err => toast(extractApiError(err, 'Fehler beim Löschen.')));
        }
    };

    const handleModalSave = () => { setShowModal(false); loadCustomers(); };

    useEffect(() => {
        let filtered = customers;
        if (companyFilter) {
            filtered = filtered.filter(c =>
                c.name.toLowerCase().includes(companyFilter.toLowerCase())
            );
        }
        setFilteredCustomers(filtered);
    }, [companyFilter, customers]);

    return (
        <div className="customers-page">

            <div className="customers-hero">
                <div>
                    <h2 className="customers-hero-title">Kunden</h2>
                    <p className="customers-hero-sub">Verwalten Sie Ihre Kundenunternehmen und Ansprechpartner</p>
                </div>
                <span className="customers-count-badge">{customers.length} Kunden</span>
            </div>

            <div className="customers-toolbar">
                <div className="customers-search-wrap">
                    <svg className="customers-search-icon" viewBox="0 0 20 20" fill="none">
                        <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.75"/>
                        <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/>
                    </svg>
                    <input
                        type="text"
                        className="customers-search"
                        placeholder="Firma suchen…"
                        value={companyFilter}
                        onChange={e => setCompanyFilter(e.target.value)}
                    />
                </div>
                <button className="button" onClick={handleAdd}>+ Kunde hinzufügen</button>
            </div>

            {isLoading ? (
                <div className="customers-empty">
                    <p>Daten werden geladen…</p>
                </div>
            ) : filteredCustomers.length === 0 ? (
                <div className="customers-empty">
                    <p>Keine Kunden gefunden.</p>
                </div>
            ) : (
                <div className="customers-grid">
                    {filteredCustomers.map(c => (
                        <div key={c.id} className="customer-card">
                            <div className="customer-card-header">
                                <div className="customer-avatar">{c.name?.[0]?.toUpperCase() ?? '?'}</div>
                                <div className="customer-card-title">
                                    <span className="customer-name">{c.name}</span>
                                </div>
                            </div>

                            <div className="customer-card-body">
                                {c.address && (
                                    <div className="customer-detail-row">
                                        <svg viewBox="0 0 20 20" fill="none" className="customer-detail-icon">
                                            <path d="M10 2a6 6 0 0 1 6 6c0 4-6 10-6 10S4 12 4 8a6 6 0 0 1 6-6Z" stroke="currentColor" strokeWidth="1.5"/>
                                            <circle cx="10" cy="8" r="2" stroke="currentColor" strokeWidth="1.5"/>
                                        </svg>
                                        <span>{c.address}</span>
                                    </div>
                                )}
                                {c.contactPerson && (
                                    <div className="customer-detail-row">
                                        <svg viewBox="0 0 20 20" fill="none" className="customer-detail-icon">
                                            <circle cx="10" cy="7" r="3" stroke="currentColor" strokeWidth="1.5"/>
                                            <path d="M3 17c0-3.314 3.134-6 7-6s7 2.686 7 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                        </svg>
                                        <span>{c.contactPerson}</span>
                                    </div>
                                )}
                                {c.email && (
                                    <div className="customer-detail-row">
                                        <svg viewBox="0 0 20 20" fill="none" className="customer-detail-icon">
                                            <rect x="2" y="5" width="16" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                                            <path d="M2 7l8 5 8-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                        </svg>
                                        <span>{c.email}</span>
                                    </div>
                                )}
                                {c.phone && (
                                    <div className="customer-detail-row">
                                        <svg viewBox="0 0 20 20" fill="none" className="customer-detail-icon">
                                            <path d="M6 2h3l1.5 4-2 1.5a10 10 0 0 0 4 4L14 9.5 18 11v3a2 2 0 0 1-2 2C7.16 16 4 8.84 4 4a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                        <span>{c.phone}</span>
                                    </div>
                                )}
                                {c.vatId && (
                                    <div className="customer-detail-row">
                                        <svg viewBox="0 0 20 20" fill="none" className="customer-detail-icon">
                                            <rect x="3" y="4" width="14" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                                            <path d="M7 8h6M7 11h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                        </svg>
                                        <span className="customer-detail-muted">USt-Id: {c.vatId}</span>
                                    </div>
                                )}
                                {c.comment && (
                                    <div className="customer-detail-row customer-comment">
                                        <svg viewBox="0 0 20 20" fill="none" className="customer-detail-icon">
                                            <path d="M4 4h12v9H4z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                                            <path d="M8 17l2-4h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                        <span>{c.comment}</span>
                                    </div>
                                )}
                            </div>

                            <div className="customer-card-actions">
                                <button className="button-warning customer-btn" onClick={() => handleEdit(c)}>Bearbeiten</button>
                                <button className="button-danger customer-btn" onClick={() => handleDelete(c.id)}>Löschen</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showModal && (
                <CustomerModal
                    customer={editCustomer}
                    onClose={() => setShowModal(false)}
                    onSave={handleModalSave}
                />
            )}
        </div>
    );
}